import {selectRemoteSourcePrompt} from './prompts.js'
import {LocalSource, RemoteSource} from './identifiers.js'
import {IdentifiersExtensions} from '../../models/app/identifiers.js'

export interface ManualMatchResult {
  identifiers: IdentifiersExtensions
  toCreate: LocalSource[]
  onlyRemote: RemoteSource[]
}

/**
 * Prompt the user to manually match each of the local sources to a remote source.
 * Sources can either be extensions or functions.
 *
 * The user can also select to create a new remote source instead of selecting an existing one.
 * Manual matching will only show sources of the same type as possible matches.
 * At the end of this process, all remote sources must be matched with the local sources to succeed.
 *
 * @param local - The local sources to match
 * @param remote - The remote sources to match
 * @returns The result of the manual matching
 */
export async function manualMatchIds(options: {
  local: LocalSource[]
  remote: RemoteSource[]
}): Promise<ManualMatchResult> {
  const identifiers: {[key: string]: string} = {}
  let pendingRemote = options.remote
  let pendingLocal = options.local

  for (const currentLocal of options.local) {
    const remoteSourcesOfSameType = pendingRemote.filter(
      (remoteSource) => remoteSource.type === currentLocal.graphQLType,
    )
    if (remoteSourcesOfSameType.length === 0) continue
    // eslint-disable-next-line no-await-in-loop
    const selected = await selectRemoteSourcePrompt(currentLocal, remoteSourcesOfSameType)
    if (!selected) continue

    identifiers[currentLocal.localIdentifier] = selected.uuid
    pendingRemote = pendingRemote.filter((remote) => remote.uuid !== selected.uuid)
    pendingLocal = pendingLocal.filter((local) => local.localIdentifier !== currentLocal.localIdentifier)
  }

  return {
    identifiers,
    toCreate: pendingLocal,
    onlyRemote: pendingRemote,
  }
}
